import { memo, useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { AlertTriangle, CalendarClock, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react'
import { useActiveChannelId } from '../../store/index.js'
import { programsApi, type PremiereAlertKind, type PremiereCalendarEntry } from '../../lib/api.js'
import { formatDurationShort } from '../../lib/utils.js'
import { isInMonth, monthGridDays, monthKeyOf, monthLabel, shiftMonth } from '../../lib/monthGrid.js'
import { PageHeader } from '../common/PageChrome.js'
import { Spinner } from '../common/Spinner.js'
import './PremiereCalendarView.css'

/**
 * Calendrier des prochains inédits, pour la direction de production.
 *
 * La grille et le catalogue répondent chacun à une moitié de la question : la
 * grille dit ce qui est posé, le catalogue dit ce qui existe. Cette vue réunit
 * les deux sur le même mois pour répondre à la question de production — « qu'y
 * a-t-il à livrer, et pour quand » — et signale ce qui empêcherait une
 * diffusion : pas encore en grille, média absent, fiche EPG incomplète, droits
 * terminés.
 */

const ALERT_LABELS: Record<PremiereAlertKind, string> = {
  NOT_SCHEDULED: 'Pas encore en grille',
  MEDIA_MISSING: 'Média manquant',
  EPG_INCOMPLETE: 'Fiche EPG incomplète',
  RIGHTS_EXPIRED: 'Droits expirés',
}

/** Les alertes se lisent par gravité : ce qui empêche l'antenne d'abord. */
const ALERT_TONES: Record<PremiereAlertKind, 'danger' | 'warning'> = {
  MEDIA_MISSING: 'danger',
  RIGHTS_EXPIRED: 'danger',
  NOT_SCHEDULED: 'warning',
  EPG_INCOMPLETE: 'warning',
}

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']

/** Une case n'affiche que les premiers inédits ; au-delà, le jour renvoie au
 *  panneau de détail plutôt que de s'étirer et de déformer la grille. */
const MAX_CHIPS_PER_DAY = 3

function todayKey(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

function dayNumber(day: string): string {
  return String(Number(day.slice(8, 10)))
}

function dayLabel(day: string): string {
  const [year, month, date] = day.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, date))
    .toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })
}

function timeLabel(entry: PremiereCalendarEntry): string {
  if (!entry.startTime) return '--:--'
  return new Date(entry.startTime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}

/** Date de première diffusion portée par la ligne : posée à l'antenne quand la
 *  case existe, seulement prévue tant qu'elle n'existe pas. La nuance est le
 *  fond du sujet pour la production — l'une est un horaire, l'autre un
 *  engagement à tenir. */
function premiereDateLabel(entry: PremiereCalendarEntry): string {
  const [year, month, date] = entry.day.split('-').map(Number)
  const day = new Date(Date.UTC(year, month - 1, date))
    .toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
  if (!entry.scheduled) return `1re diff. prévue le ${day} · pas de case en grille`
  return `1re diff. le ${day} à ${timeLabel(entry)}`
}

export const PremiereCalendarView = memo(function PremiereCalendarView({
  onOpenProgram,
}: {
  onOpenProgram?: (programId: string) => void
}) {
  const channelId = useActiveChannelId()
  const [month, setMonth] = useState(() => monthKeyOf(new Date()))
  const [entries, setEntries] = useState<PremiereCalendarEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [onlyAlerts, setOnlyAlerts] = useState(false)
  const [selectedDay, setSelectedDay] = useState<string | null>(null)

  const days = useMemo(() => monthGridDays(month), [month])
  const today = useMemo(() => todayKey(), [])

  const load = useCallback(async () => {
    if (!channelId || days.length === 0) { setEntries([]); return }
    setLoading(true)
    setError(null)
    try {
      setEntries(await programsApi.premiereCalendar({
        channelId,
        from: days[0],
        to: days[days.length - 1],
      }).then(result => result.entries))
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : String(loadError))
      setEntries([])
    } finally {
      setLoading(false)
    }
  }, [channelId, days])

  useEffect(() => { load() }, [load])

  const visibleEntries = useMemo(
    () => onlyAlerts ? entries.filter(entry => entry.alerts.length > 0) : entries,
    [entries, onlyAlerts],
  )

  const byDay = useMemo(() => {
    const map = new Map<string, PremiereCalendarEntry[]>()
    for (const entry of visibleEntries) {
      const bucket = map.get(entry.day)
      if (bucket) bucket.push(entry)
      else map.set(entry.day, [entry])
    }
    return map
  }, [visibleEntries])

  // Synthèse du mois affiché seulement : les jours débordants appartiennent aux
  // mois voisins et fausseraient le compte annoncé sous le titre.
  const summary = useMemo(() => {
    const inMonth = entries.filter(entry => isInMonth(entry.day, month))
    return {
      total: inMonth.length,
      alerting: inMonth.filter(entry => entry.alerts.length > 0).length,
      unscheduled: inMonth.filter(entry => !entry.scheduled).length,
    }
  }, [entries, month])

  // La vue ne montre que les diffusions à venir : reculer avant le mois en
  // cours n'afficherait qu'un calendrier vide, ce qui se lirait comme une
  // panne. Le bouton s'éteint plutôt que de mentir.
  const currentMonth = useMemo(() => monthKeyOf(new Date()), [])
  const canGoBack = month > currentMonth
  const handlePrevious = useCallback(() => { setSelectedDay(null); setMonth(current => shiftMonth(current, -1)) }, [])
  const handleNext = useCallback(() => { setSelectedDay(null); setMonth(current => shiftMonth(current, 1)) }, [])
  const handleToday = useCallback(() => { setSelectedDay(null); setMonth(monthKeyOf(new Date())) }, [])
  const handleToggleAlerts = useCallback(() => setOnlyAlerts(current => !current), [])
  const handleSelectDay = useCallback((day: string) => {
    setSelectedDay(current => current === day ? null : day)
  }, [])

  const selectedEntries = selectedDay ? byDay.get(selectedDay) ?? [] : []

  return (
    <div className="tp-page-shell premiere-calendar">
      <PageHeader
        title="Prochains inédits"
        actions={(
          <>
            <button type="button" className="tp-button" onClick={handleToday}>
              <CalendarClock size={14} aria-hidden="true" />
              Mois en cours
            </button>
            <button
              type="button"
              className="tp-button"
              onClick={load}
              disabled={!channelId || loading}
              data-state={loading ? 'loading' : undefined}
            >
              <RefreshCw size={13} aria-hidden="true" />
              {loading ? 'Actualisation…' : 'Actualiser'}
            </button>
          </>
        )}
      />

      <section className="premiere-controls" aria-label="Mois affiché et synthèse">
        <div className="premiere-month-nav">
          <button type="button" className="tp-icon-button" onClick={handlePrevious} disabled={!canGoBack} aria-label="Mois précédent">
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <h2 className="premiere-month-label">{monthLabel(month)}</h2>
          <button type="button" className="tp-icon-button" onClick={handleNext} aria-label="Mois suivant">
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>

        <div className="premiere-summary" aria-live="polite">
          <MiniStat label="Inédits" value={loading ? '…' : String(summary.total)} />
          <MiniStat label="Sans case en grille" value={loading ? '…' : String(summary.unscheduled)} tone={summary.unscheduled ? 'warning' : undefined} />
          <MiniStat label="À traiter" value={loading ? '…' : String(summary.alerting)} tone={summary.alerting ? 'danger' : undefined} />
        </div>

        <label className="premiere-toggle">
          <input type="checkbox" checked={onlyAlerts} onChange={handleToggleAlerts} />
          <span>N’afficher que ce qui a une alerte</span>
        </label>
      </section>

      {error && <div className="tp-feedback tp-feedback--error premiere-error" role="alert">{error}</div>}

      <main className="premiere-main">
        <div className="premiere-grid-wrapper" aria-busy={loading}>
          <div className="premiere-weekdays" aria-hidden="true">
            {WEEKDAYS.map(label => <span key={label} className="premiere-weekday">{label}</span>)}
          </div>
          <div className="premiere-grid" role="grid" aria-label={`Prochains inédits de ${monthLabel(month)}`}>
            {days.map(day => (
              <DayCell
                key={day}
                day={day}
                entries={byDay.get(day) ?? []}
                outside={!isInMonth(day, month)}
                past={day < today}
                today={day === today}
                selected={day === selectedDay}
                onSelect={handleSelectDay}
              />
            ))}
          </div>
          {loading && <div className="premiere-loading"><Spinner /></div>}
        </div>

        <aside className="premiere-detail" aria-label="Détail du jour">
          {selectedDay ? (
            <>
              <h3 className="premiere-detail__title">{dayLabel(selectedDay)}</h3>
              {selectedEntries.length === 0 && <p className="premiere-detail__empty">Aucun inédit ce jour-là.</p>}
              <ul className="premiere-detail__list">
                {selectedEntries.map(entry => (
                  <DetailRow key={`${entry.programId}-${entry.day}`} entry={entry} onOpenProgram={onOpenProgram} />
                ))}
              </ul>
            </>
          ) : (
            <p className="premiere-detail__empty">
              Choisissez un jour pour voir le détail des inédits à venir et ce qu’il reste à livrer.
            </p>
          )}
        </aside>
      </main>
    </div>
  )
})

const MiniStat = memo(function MiniStat({
  label, value, tone,
}: { label: string; value: string; tone?: 'warning' | 'danger' }) {
  return (
    <div className="premiere-stat" data-tone={tone}>
      <span className="premiere-stat__value">{value}</span>
      <span className="premiere-stat__label">{label}</span>
    </div>
  )
})

const DayCell = memo(function DayCell({
  day, entries, outside, past, today, selected, onSelect,
}: {
  day: string
  entries: PremiereCalendarEntry[]
  outside: boolean
  /** Journée révolue : la vue ne la remplit jamais, elle reste là pour que la
   *  semaine garde ses sept cases. */
  past: boolean
  today: boolean
  selected: boolean
  onSelect: (day: string) => void
}) {
  const handleClick = useCallback(() => onSelect(day), [day, onSelect])
  const hidden = Math.max(0, entries.length - MAX_CHIPS_PER_DAY)

  return (
    <button
      type="button"
      role="gridcell"
      className="premiere-day"
      data-outside={outside || undefined}
      data-past={past || undefined}
      data-today={today || undefined}
      aria-pressed={selected}
      aria-label={`${dayLabel(day)} — ${entries.length} inédit(s)`}
      onClick={handleClick}
    >
      <span className="premiere-day__head">
        <span className="premiere-day__number">{dayNumber(day)}</span>
        {entries.length > 0 && <span className="premiere-day__count">{entries.length}</span>}
      </span>
      <span className="premiere-day__list">
        {entries.slice(0, MAX_CHIPS_PER_DAY).map(entry => (
          <EntryChip key={`${entry.programId}-${entry.day}`} entry={entry} />
        ))}
        {hidden > 0 && <span className="premiere-day__more">+{hidden} autre{hidden > 1 ? 's' : ''}</span>}
      </span>
    </button>
  )
})

const EntryChip = memo(function EntryChip({ entry }: { entry: PremiereCalendarEntry }) {
  const worst = worstAlert(entry.alerts)
  return (
    <span
      className="premiere-chip"
      data-unscheduled={entry.scheduled ? undefined : true}
      data-live={entry.isLive || undefined}
      style={entry.categoryColor ? { '--premiere-accent': entry.categoryColor } as CSSProperties : undefined}
      title={entry.alerts.length ? `${entry.title} — ${entry.alerts.map(alert => ALERT_LABELS[alert]).join(', ')}` : entry.title}
    >
      <span className="premiere-chip__time">{timeLabel(entry)}</span>
      <span className="premiere-chip__title">{entry.title}</span>
      {worst && (
        <AlertTriangle
          className="premiere-chip__alert"
          data-tone={ALERT_TONES[worst]}
          size={11}
          aria-label={ALERT_LABELS[worst]}
        />
      )}
    </span>
  )
})

const DetailRow = memo(function DetailRow({
  entry, onOpenProgram,
}: { entry: PremiereCalendarEntry; onOpenProgram?: (programId: string) => void }) {
  const handleOpen = useCallback(() => onOpenProgram?.(entry.programId), [entry.programId, onOpenProgram])
  return (
    <li className="premiere-detail__row">
      <div className="premiere-detail__head">
        <span className="premiere-detail__time">{timeLabel(entry)}</span>
        <span className="premiere-detail__name">{entry.title}</span>
        {entry.isLive && <span className="premiere-tag premiere-tag--live">Direct</span>}
      </div>
      <div className="premiere-detail__meta">{premiereDateLabel(entry)}</div>
      <div className="premiere-detail__meta">
        {entry.seriesTitle ? `${entry.seriesTitle} · ` : ''}
        {entry.categoryLabel ? `${entry.categoryLabel} · ` : ''}
        {formatDurationShort(entry.durationSec)}
      </div>
      {entry.alerts.length > 0 && (
        <div className="premiere-detail__alerts">
          {entry.alerts.map(alert => (
            <span key={alert} className="premiere-tag" data-tone={ALERT_TONES[alert]}>
              <AlertTriangle size={10} aria-hidden="true" />
              {ALERT_LABELS[alert]}
            </span>
          ))}
        </div>
      )}
      {onOpenProgram && (
        <button type="button" className="tp-button premiere-detail__open" onClick={handleOpen}>
          Ouvrir la fiche
        </button>
      )}
    </li>
  )
})

/** L'icône d'une pastille ne porte qu'une alerte : la plus grave. */
function worstAlert(alerts: PremiereAlertKind[]): PremiereAlertKind | null {
  if (alerts.length === 0) return null
  return alerts.find(alert => ALERT_TONES[alert] === 'danger') ?? alerts[0]
}
